import { Box, Container, Paper, Stack, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import PolicyModal, { Policy } from '../Register/PolicyModal';
import PolicyDocumentActions from './PolicyDocumentActions';

const sectionTitleStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
};

const paragraphStyle = {
    display: 'block',
    marginTop: '10px',
    marginBottom: '10px',
};

const TermsOfService = () => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [policy, setPolicy] = useState(Policy.PrivacyPolicy);
    const handlePolicyModal = (p: Policy | ((prevState: Policy) => Policy)) => {
        setPolicy(p);
        setModalOpen(true);
    };
    return (
        <Container maxWidth='lg' sx={{ mt: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, md: 5 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                }}
            >
                <Stack spacing={3} ref={contentRef}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 2,
                        }}
                    >
                        <Typography
                            variant='h1'
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                textAlign: 'left',
                                textTransform: 'uppercase',
                                fontSize: {
                                    xs: '0.95rem',
                                    sm: '1.25rem',
                                    md: '1.8rem',
                                },
                                textUnderlineOffset: { xs: '4px', md: '8px' },
                                whiteSpace: { xs: 'nowrap', md: 'normal' },
                            }}
                        >
                            Terms of Service
                        </Typography>
                        <PolicyDocumentActions
                            contentRef={contentRef}
                            title='Terms of Service'
                        />
                    </Box>
                    <Box>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            Effective: September 20, 2023
                        </Typography>
                    </Box>

                    <Typography variant='body3' sx={{ ...paragraphStyle }}>
                        These Terms take effect when you register an account
                        with us or access or use any 7DAY cloud product. You do
                        not need to sign these Terms for them to be binding. By
                        signing up, creating an account, placing an order, or
                        using any part of our services (including No-Charge
                        Products), you confirm that you’ve read, understood, and
                        agree to be bound by these Terms.
                    </Typography>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            End user consent and responsibility
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            Our platform supports different ways for users to
                            get started. Some users may be invited by an
                            Administrator, while others may sign up individually
                            and later be connected to a team or organization. As
                            an Administrator, you are responsible for managing
                            your account settings and determining who is allowed
                            to become an End User. If payment is required,
                            access will only be granted to End Users for whom
                            the applicable fees have been paid.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Credentials
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            End Users must keep their login credentials strictly
                            confidential and must not share them with anyone.
                            Accounts are assigned to individual users and cannot
                            be shared. You are responsible for all activity that
                            occurs under your End Users’ accounts and must
                            notify us immediately if you become aware of any
                            unauthorized access or use.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Age requirements for end users
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            Our platform is not intended for use by anyone under
                            the age of 16. You are responsible for ensuring that
                            all End Users meet this minimum age requirement.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Host Payments Terms
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            View{' '}
                            <Box
                                component='span'
                                role='button'
                                tabIndex={0}
                                onClick={() =>
                                    handlePolicyModal(Policy.TermsOfPayment)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ')
                                        handlePolicyModal(
                                            Policy.TermsOfPayment,
                                        );
                                }}
                                sx={{
                                    fontStyle: 'italic',
                                    textDecoration: 'underlined',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                }}
                            >
                                Terms of Payment
                            </Box>
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Guest Billing and Payment Terms
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            View{' '}
                            <Box
                                component='span'
                                role='button'
                                tabIndex={0}
                                onClick={() =>
                                    handlePolicyModal(Policy.TermsOfPayment)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ')
                                        handlePolicyModal(
                                            Policy.TermsOfPayment,
                                        );
                                }}
                                sx={{
                                    fontStyle: 'italic',
                                    textDecoration: 'underlined',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                }}
                            >
                                Terms of Payment
                            </Box>
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Contractor (Partner Services)
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            View{' '}
                            <Box
                                component='span'
                                role='button'
                                tabIndex={0}
                                onClick={() =>
                                    handlePolicyModal(Policy.TermsOfPayment)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ')
                                        handlePolicyModal(
                                            Policy.TermsOfPayment,
                                        );
                                }}
                                sx={{
                                    fontStyle: 'italic',
                                    textDecoration: 'underlined',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                }}
                            >
                                Terms of Payment
                            </Box>
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Service Fees
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            When a Reservation is made, 7DAY charges a service
                            fee to both the Host and the Guest:
                            <Stack component='ul' sx={paragraphStyle}>
                                <Typography variant='body3' component='li'>
                                    Host Fee: A service fee of the Host’s total
                                    Reservation value is deducted at the time
                                    the Reservation is made. This does not apply
                                    to additional Add-ons, Expense Claims and
                                    Partner Services incurred during the Guest’s
                                    stay unless otherwise stated.
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Guest Fee: A guest service fee is added to
                                    every new Reservation made by the Guest.
                                    This does not apply to additional Add-ons
                                    applied to the original Reservation, Expense
                                    Claims and Partner Services incurred during
                                    the Guest’s stay unless otherwise stated.
                                </Typography>
                            </Stack>
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            These fees help us maintain and improve the 7DAY
                            platform and provide support services for both Hosts
                            and Guests.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Support
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            During your Subscription Term, we’ll provide support
                            as long as your account is registered with 7DAY and
                            your organisation has granted you access to the
                            platform.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Restrictions
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            Unless expressly allowed under these Terms, you must
                            not: (a) copy, modify, or create derivative works of
                            the platform; (b) rent, lease, sell, sublicense,
                            transfer, or share access with third parties; (c)
                            use the platform for the benefit of anyone outside
                            your organisation; (d) incorporate the platform into
                            any product or service you offer to others; (e)
                            bypass or interfere with usage limits or security
                            features; (f) reverse engineer, decompile, or
                            attempt to access the source code or underlying
                            systems, except where allowed by law (and only after
                            giving us notice); (g) remove or hide any
                            proprietary notices; (h) use the platform to analyse
                            or build competing products; or (i) help or
                            encourage anyone else to do any of the above.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Privacy
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            We collect certain information about you and your
                            End Users as part of providing access to the
                            platform and under these Terms. All data is
                            collected and used in line with our{' '}
                            <Box
                                component='span'
                                role='button'
                                tabIndex={0}
                                onClick={() =>
                                    handlePolicyModal(Policy.PrivacyPolicy)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ')
                                        handlePolicyModal(Policy.PrivacyPolicy);
                                }}
                                sx={{
                                    fontStyle: 'italic',
                                    textDecoration: 'underlined',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                }}
                            >
                                Privacy Policy
                            </Box>
                            , which you acknowledge and agree to.
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Reviews and User Content
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            Users may submit reviews, ratings, comments, photos,
                            testimonials, or other content relating to
                            Experiences, Add-ons, Partner Services, Hosts,
                            Guests, or services available through the platform.
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            When submitting a review, users may have the option
                            to publish the review publicly using their first
                            name, last name initial, and profile photo, or to
                            publish the review anonymously where such
                            functionality is available. By choosing to publish a
                            review publicly, you acknowledge that the selected
                            information may be visible to other users of the
                            platform and visitors to the 7DAY website.
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            Reviews and user content must be genuine, accurate,
                            and based on an actual experience. Users must not
                            submit content that is misleading, defamatory,
                            offensive, discriminatory, fraudulent, unlawful, or
                            that infringes the rights of any person.
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            7DAY reserves the right to review, moderate, edit,
                            hide, remove, or refuse to publish any review or
                            user content that breaches these Terms, is
                            inappropriate, or may harm the platform, its users,
                            Hosts, Guests, or third parties.
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            By submitting a review or other user content, you
                            grant 7DAY a non-exclusive, worldwide, royalty-free
                            licence to display, reproduce, publish, distribute,
                            and use that content in connection with operating,
                            promoting, and improving the platform.
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Subpoenas
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            We may disclose your data if required by law,
                            subpoena, or court order. Where legally permitted,
                            we’ll make reasonable efforts to notify you. At
                            7DAY, we aim to balance your privacy rights with our
                            legal obligations.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleStyle} gutterBottom>
                            Termination
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            You may stop using the platform and terminate these
                            Terms (including any Orders or Reservations) at any
                            time by giving us written notice. If you do, (i) any
                            pre-paid fees are non-refundable, and (ii) any
                            unpaid fees for the current Subscription Term or
                            related services will become immediately due.
                        </Typography>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            When these Terms expire or are terminated, you must
                            stop using the platform and delete (or return, if we
                            request) all of our Confidential Information,
                            including any stored on third-party systems used on
                            your behalf. If requested, you must confirm that
                            deletion in writing. After termination or expiry,
                            you won’t have access to your data, and we may
                            delete it unless we're legally required to retain
                            it—so please make sure to export your data during
                            your active Subscription Term. Termination does not
                            affect any fees owed up to the effective termination
                            date. Unless otherwise specified, terminating these
                            Terms does not limit either party’s right to seek
                            other legal remedies.
                        </Typography>
                    </Box>
                </Stack>
            </Paper>

            <PolicyModal
                policy={policy}
                open={modalOpen}
                onClose={() => setModalOpen(false)}
            />
        </Container>
    );
};
export default TermsOfService;
